import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "../api/baseQuery";

export type CallDashboardStats = {
  total_calls: number;
  urgent_calls: number;
  new_patients: number;
  existing_patients: number;
  /** Calls per skill code in the selected window, for the counts on each chip. */
  skill_counts: Record<string, number>;
};

export type CallDashboardDateRange = "all_time" | "today" | "yesterday" | "last_7_days";

export type CallDashboardQueryArgs = {
  page?: number;
  /** Day filter, resolved server-side against `tz`. */
  range?: CallDashboardDateRange;
  /** IANA timezone the day filter is measured in. */
  tz?: string;
  /** Skill code to filter by, applied across all pages. */
  skill?: string;
};

export type CallSkill = {
  id: number;
  code: string;
  label: string;
  /**
   * Analysis field a rule-driven skill matches on (e.g. "provider"). Empty for the
   * built-in skills. Chips are grouped into their own labelled row per source_field.
   */
  source_field?: string;
};

export type CallDashboardCall = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  date: string; // ISO timestamp
  // New fields
  duration: number | null;
  summary: string | null;
  from_number: string | null;
  skills: CallSkill[];
  /** Latest insurance eligibility check made during this call, if any. */
  eligibility: {
    status: "success" | "rejected" | "error" | "pending";
    payer: string;
    plan: string;
    copay: string;
    mismatch: string;
    summary: string;
    checked_at: string;
  } | null;
};

export type PaginationInfo = {
  count: number;
  next: string | null;
  previous: string | null;
  page_size: number;
};

export type CallDashboardData = {
  calls: CallDashboardCall[];
  stats: CallDashboardStats;
  pagination: PaginationInfo;
};

export type CallSkillsResponse = {
  skills: CallSkill[];
};

export type CallTranscriptResponse = {
  id: number;
  transcript: string;
  call_identifier: string | null;
};

export const callsApi = createApi({
  reducerPath: "callsApi",
  tagTypes: ["CallDashboard"],
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getCallDashboardData: build.query<CallDashboardData, CallDashboardQueryArgs>({
      query: ({ page = 1, range = "all_time", tz, skill } = {}) => ({
        url: `/ai_admin/calls/dashboard/`,
        params: { page, range, tz, skill },
      }),
      providesTags: [{ type: "CallDashboard", id: "DATA" }],
    }),
    getArchivedCallDashboardData: build.query<CallDashboardData, CallDashboardQueryArgs>({
      query: ({ page = 1, range = "all_time", tz, skill } = {}) => ({
        url: `/ai_admin/calls/dashboard/`,
        params: { page, range, tz, skill, archived: true },
      }),
      providesTags: [{ type: "CallDashboard", id: "ARCHIVED" }],
    }),
    archiveCall: build.mutation<{ status: string }, number>({
      query: (callId) => ({
        url: `/ai_admin/calls/${callId}/archive/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CallDashboard", id: "DATA" },
        { type: "CallDashboard", id: "ARCHIVED" },
      ],
    }),
    unarchiveCall: build.mutation<{ status: string }, number>({
      query: (callId) => ({
        url: `/ai_admin/calls/${callId}/unarchive/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CallDashboard", id: "DATA" },
        { type: "CallDashboard", id: "ARCHIVED" },
      ],
    }),
    getCallSkills: build.query<CallSkillsResponse, void>({
      query: () => `/ai_admin/skills/`,
    }),
    getCallTranscript: build.query<CallTranscriptResponse, number>({
      query: (callId) => `/ai_admin/calls/${callId}/transcript/`,
    }),
  }),
});

export const {
  useGetCallDashboardDataQuery,
  useGetArchivedCallDashboardDataQuery,
  useArchiveCallMutation,
  useUnarchiveCallMutation,
  useGetCallSkillsQuery,
  useGetCallTranscriptQuery,
} = callsApi;
